// @ts-nocheck
const MOTION_READY_ATTR = 'data-motion-ready';
const MOTION_DISABLED_SELECTOR = '[data-no-motion], .no-motion';
const motionSettings = window.ThemeMotion?.presets?.[window.ThemeMotion?.preset] || {};
const durationScale = motionSettings.gsapDurationScale ?? 1;
const distanceScale = motionSettings.gsapDistanceScale ?? 1;
const staggerScale = motionSettings.gsapStaggerScale ?? 1;

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (!prefersReducedMotion) {
  setupAnimationBoot();
}

function setupAnimationBoot() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAnimations, { once: true });
  } else {
    bootAnimations();
  }

  // Re-run only on explicit Shopify section lifecycle events.
  document.addEventListener('shopify:section:load', () => queueHydrate());
  document.addEventListener('shopify:section:reorder', () => queueHydrate());
}

function bootAnimations() {
  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  if (!gsap || !ScrollTrigger) return;

  gsap.registerPlugin(ScrollTrigger);
  animateHeader();
  hydrateAnimations(document);
  ScrollTrigger.refresh(true);
  window.lenis?.resize();

  // Late refreshes: fonts, images, and Lenis often settle after DOMContentLoaded,
  // and pinned horizontal scrollers (like product-about) measure the viewport at
  // setup time. Without these refreshes the pin can fail to engage on some
  // screen sizes, leaving the section to scroll past with all slides visible.
  scheduleLateRefresh();
  observeProductAboutResize();
}

function scheduleLateRefresh() {
  const ScrollTrigger = window.ScrollTrigger;
  if (!ScrollTrigger) return;

  const refresh = () => {
    ScrollTrigger.refresh(true);
    window.lenis?.resize();
  };

  if (document.readyState === 'complete') {
    requestAnimationFrame(refresh);
  } else {
    window.addEventListener('load', () => requestAnimationFrame(refresh), { once: true });
  }

  if (document.fonts && typeof document.fonts.ready?.then === 'function') {
    document.fonts.ready.then(() => requestAnimationFrame(refresh)).catch(() => {});
  }

  // Backstop: a final settle pass after everything else has had a chance to lay out.
  setTimeout(refresh, 600);
}

function observeProductAboutResize() {
  if (typeof ResizeObserver === 'undefined') return;
  const ScrollTrigger = window.ScrollTrigger;
  if (!ScrollTrigger) return;

  const sections = document.querySelectorAll('.product-about-rows');
  if (!sections.length) return;

  let pending = false;
  const observer = new ResizeObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      ScrollTrigger.refresh();
    });
  });

  sections.forEach((section) => observer.observe(section));
}

let rafId = null;
function queueHydrate() {
  if (rafId) cancelAnimationFrame(rafId);
  rafId = requestAnimationFrame(() => {
    hydrateAnimations(document);
    window.ScrollTrigger?.refresh();
    window.lenis?.resize();
    rafId = null;
  });
}

function hydrateAnimations(root) {
  animateHero(root);
  animateAnnouncement(root);
  animateCards(root);
  animateProductInformation(root);
  animateProductAbout(root);
  animateAboutKenz(root);
  animateContactKenz(root);
  animateStoreLocatorKenz(root);
  animateStoreReviewsKenz(root);
  animateAllCollectionsKenz(root);
  animateFooter(root);
}

function shouldSkipNode(node) {
  return node.hasAttribute(MOTION_READY_ATTR) || Boolean(node.closest(MOTION_DISABLED_SELECTOR));
}

function revealOnScroll(node, options = {}) {
  if (shouldSkipNode(node) || !window.gsap) return;
  const { y = 18, duration = 0.5, delay = 0, start = 'top 90%' } = options;

  node.setAttribute(MOTION_READY_ATTR, 'true');
  window.gsap.fromTo(
    node,
    { autoAlpha: 0, y: y * distanceScale },
    {
      autoAlpha: 1,
      y: 0,
      duration: duration * durationScale,
      delay: delay * durationScale,
      ease: 'power1.out',
      overwrite: 'auto',
      clearProps: 'transform,opacity,visibility',
      scrollTrigger: {
        trigger: node,
        start,
        once: true,
      },
    }
  );
}

function animateHeader() {
  const rows = document.querySelectorAll('.header__row');
  if (!rows.length || !window.gsap) return;

  rows.forEach((row, index) => {
    if (shouldSkipNode(row)) return;
    row.setAttribute(MOTION_READY_ATTR, 'true');
    window.gsap.fromTo(
      row,
      { autoAlpha: 0, y: -10 },
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.34 * durationScale,
        delay: index * 0.05 * durationScale,
        ease: 'power1.out',
        clearProps: 'transform,opacity,visibility',
      }
    );
  });
}

function animateHero(root) {
  root
    .querySelectorAll('.hero, .slideshow, .layered-slideshow, .marquee, .carousel')
    .forEach((node) => revealOnScroll(node, { y: 24, duration: 0.62, start: 'top 94%' }));
}

function animateAnnouncement(root) {
  root.querySelectorAll('.announcement-bar, announcement-bar-component').forEach((node) => {
    revealOnScroll(node, { y: -8, duration: 0.3, start: 'top 99%' });
  });
}

function animateCards(root) {
  if (!window.gsap) return;
  root.querySelectorAll('.product-grid, .resource-list, .editorial-product-grid').forEach((grid) => {
    if (shouldSkipNode(grid)) return;
    const cards = grid.querySelectorAll('.product-grid__item, .product-card, .resource-card');
    if (!cards.length) return;

    grid.setAttribute(MOTION_READY_ATTR, 'true');
    window.gsap.fromTo(
      cards,
      { autoAlpha: 0, y: 14 * distanceScale },
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.42 * durationScale,
        stagger: 0.035 * staggerScale,
        ease: 'power1.out',
        overwrite: 'auto',
        clearProps: 'transform,opacity,visibility',
        scrollTrigger: { trigger: grid, start: 'top 91%', once: true },
      }
    );
  });
}

function animateProductInformation(root) {
  root
    .querySelectorAll('.product-information__media, .product-details, .pdp-block, .sticky-add-to-cart__bar, .product-recommendations')
    .forEach((node) => revealOnScroll(node, { y: 16, duration: 0.5, start: 'top 92%' }));
}

function syncProductAboutSlideWidths(viewport, slides) {
  const measured = Math.round(viewport.getBoundingClientRect().width || viewport.clientWidth || 0);
  // If the viewport hasn't been laid out yet (returns 0), don't write
  // pixel widths — the CSS fallback (flex: 0 0 100%) keeps slides at
  // viewport width until measurements become valid.
  if (measured <= 1) {
    slides.forEach((slide) => {
      slide.style.removeProperty('flex');
      slide.style.removeProperty('width');
      slide.style.removeProperty('min-width');
    });
    return;
  }
  slides.forEach((slide) => {
    slide.style.flex = `0 0 ${measured}px`;
    slide.style.width = `${measured}px`;
    slide.style.minWidth = `${measured}px`;
  });
}

function measureProductAboutScroll(viewport, track) {
  const viewportWidth = viewport.clientWidth || viewport.getBoundingClientRect().width || 0;
  return Math.max(1, track.scrollWidth - viewportWidth);
}

function animateProductAbout(root) {
  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  if (!gsap || !ScrollTrigger) return;

  root.querySelectorAll('.product-about-rows').forEach((section) => {
    const pinHost = section.querySelector('.product-about-rows__pin');
    const viewport = section.querySelector('.product-about-rows__viewport');
    const track = section.querySelector('.product-about-rows__track');
    const slides = section.querySelectorAll('.product-about-rows__slide');
    const progressFill = section.querySelector('.product-about-rows__progress-fill');
    const scrollCue = section.querySelector('.product-about-rows__scroll-cue');
    if (!pinHost || !viewport || !track) return;

    if (section.__aboutSliderTween) {
      section.__aboutSliderTween.scrollTrigger?.kill();
      section.__aboutSliderTween.kill();
      section.__aboutSliderTween = null;
    }

    gsap.set(track, { clearProps: 'transform,x,xPercent' });
    gsap.set(pinHost, { clearProps: 'transform' });
    if (progressFill) gsap.set(progressFill, { scaleX: 0 });
    if (scrollCue) gsap.set(scrollCue, { autoAlpha: 1, y: 0 });

    syncProductAboutSlideWidths(viewport, slides);

    if (slides.length < 2) {
      return;
    }

    section.setAttribute(MOTION_READY_ATTR, 'true');

    const tween = gsap.to(track, {
      x: () => -measureProductAboutScroll(viewport, track),
      ease: 'none',
      overwrite: 'auto',
      scrollTrigger: {
        trigger: pinHost,
        pin: true,
        pinSpacing: true,
        scrub: 0.35,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        start: 'top top',
        end: () => '+=' + measureProductAboutScroll(viewport, track),
        onRefreshInit: () => syncProductAboutSlideWidths(viewport, slides),
        onRefresh: () => syncProductAboutSlideWidths(viewport, slides),
        onUpdate: (self) => {
          if (progressFill) gsap.set(progressFill, { scaleX: self.progress });
          if (scrollCue) {
            gsap.to(scrollCue, {
              autoAlpha: self.progress > 0.015 ? 0 : 1,
              y: self.progress > 0.015 ? -10 : 0,
              duration: 0.18,
              overwrite: true,
            });
          }
        },
      },
    });
    section.__aboutSliderTween = tween;
  });
}

function animateAboutKenz(root) {
  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  if (!gsap || !ScrollTrigger) return;

  root.querySelectorAll('.about-kenz').forEach((section) => {
    if (shouldSkipNode(section)) return;
    const revealItems = section.querySelectorAll('[data-about-reveal]');
    const timelineLine = section.querySelector('.about-kenz__timeline-line');
    const mediaItems = section.querySelectorAll('.about-kenz__image, .about-kenz__placeholder, .about-kenz__video-frame');

    section.setAttribute(MOTION_READY_ATTR, 'true');

    revealItems.forEach((item, index) => {
      const fromRight = index % 2 === 0;

      gsap.fromTo(
        item,
        {
          autoAlpha: 0,
          x: (fromRight ? 86 : -86) * distanceScale,
        },
        {
          autoAlpha: 1,
          x: 0,
          duration: 0.78 * durationScale,
          ease: 'power2.out',
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility',
          scrollTrigger: {
            trigger: item,
            start: 'top 84%',
            once: true,
          },
        }
      );
    });

    mediaItems.forEach((item) => {
      gsap.fromTo(
        item,
        { scale: 1.04 },
        {
          scale: 1,
          duration: 1.1 * durationScale,
          ease: 'power2.out',
          overwrite: 'auto',
          clearProps: 'transform',
          scrollTrigger: {
            trigger: item,
            start: 'top 88%',
            once: true,
          },
        }
      );
    });

    if (timelineLine) {
      gsap.fromTo(
        timelineLine,
        { scaleX: 0, scaleY: 0 },
        {
          scaleX: 1,
          scaleY: 1,
          duration: 0.9 * durationScale,
          ease: 'power1.out',
          transformOrigin: 'right center',
          scrollTrigger: {
            trigger: timelineLine,
            start: 'top 82%',
            once: true,
          },
        }
      );
    }
  });
}

function animateContactKenz(root) {
  const gsap = window.gsap;
  if (!gsap) return;

  root.querySelectorAll('.contact-kenz').forEach((section) => {
    if (shouldSkipNode(section)) return;
    const items = section.querySelectorAll(
      '.contact-kenz__eyebrow, .contact-kenz__title, .contact-kenz__intro, .contact-kenz__info-card, .contact-kenz__note, .contact-kenz__form-card'
    );

    section.setAttribute(MOTION_READY_ATTR, 'true');
    items.forEach((item, index) => {
      const fromRight = index % 2 === 0;
      gsap.fromTo(
        item,
        { autoAlpha: 0, x: (fromRight ? 56 : -56) * distanceScale },
        {
          autoAlpha: 1,
          x: 0,
          duration: 0.62 * durationScale,
          ease: 'power2.out',
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility',
          scrollTrigger: {
            trigger: item,
            start: 'top 88%',
            once: true,
          },
        }
      );
    });
  });
}

function animateStoreLocatorKenz(root) {
  const gsap = window.gsap;
  if (!gsap) return;

  root.querySelectorAll('.store-locator-kenz').forEach((section) => {
    if (shouldSkipNode(section)) return;

    const headingItems = section.querySelectorAll(
      '.store-locator-kenz__eyebrow, .store-locator-kenz__title, .store-locator-kenz__intro'
    );
    const panelItems = section.querySelectorAll(
      '.store-locator-kenz__panel-kicker, .store-locator-kenz__store-name, .store-locator-kenz__detail, .store-locator-kenz__cta, .store-locator-kenz__note'
    );
    const mapCard = section.querySelector('.store-locator-kenz__map-card');
    const ambient = section.querySelector('.store-locator-kenz__ambient');

    section.setAttribute(MOTION_READY_ATTR, 'true');

    gsap.fromTo(
      headingItems,
      { autoAlpha: 0, y: 28 * distanceScale },
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.7 * durationScale,
        stagger: 0.08 * staggerScale,
        ease: 'power2.out',
        overwrite: 'auto',
        clearProps: 'transform,opacity,visibility',
        scrollTrigger: { trigger: section, start: 'top 88%', once: true },
      }
    );

    if (mapCard) {
      gsap.fromTo(
        mapCard,
        { autoAlpha: 0, x: -70 * distanceScale, scale: 0.96 },
        {
          autoAlpha: 1,
          x: 0,
          scale: 1,
          duration: 0.86 * durationScale,
          ease: 'power2.out',
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility',
          scrollTrigger: { trigger: mapCard, start: 'top 86%', once: true },
        }
      );
    }

    gsap.fromTo(
      panelItems,
      { autoAlpha: 0, x: 54 * distanceScale },
      {
        autoAlpha: 1,
        x: 0,
        duration: 0.58 * durationScale,
        stagger: 0.055 * staggerScale,
        ease: 'power2.out',
        overwrite: 'auto',
        clearProps: 'transform,opacity,visibility',
        scrollTrigger: { trigger: section.querySelector('.store-locator-kenz__panel') || section, start: 'top 84%', once: true },
      }
    );

    if (ambient) {
      gsap.fromTo(
        ambient,
        { autoAlpha: 0.35, scale: 0.86 },
        {
          autoAlpha: 1,
          scale: 1,
          duration: 1.4 * durationScale,
          ease: 'power1.out',
          overwrite: 'auto',
          scrollTrigger: { trigger: section, start: 'top 92%', once: true },
        }
      );
    }
  });
}

function animateAllCollectionsKenz(root) {
  const gsap = window.gsap;
  if (!gsap) return;

  root.querySelectorAll('[data-all-collections-kenz]').forEach((section) => {
    if (shouldSkipNode(section)) return;

    const revealItems = section.querySelectorAll('[data-all-collections-reveal]');
    const featuredCards = section.querySelectorAll('.all-collections-kenz__featured-card');
    const gridCards = section.querySelectorAll('.all-collections-kenz__collection-card');
    const ambientItems = section.querySelectorAll('.all-collections-kenz__ambient');

    section.setAttribute(MOTION_READY_ATTR, 'true');

    gsap.fromTo(
      revealItems,
      { autoAlpha: 0, y: 30 * distanceScale },
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.72 * durationScale,
        stagger: 0.08 * staggerScale,
        ease: 'power2.out',
        overwrite: 'auto',
        clearProps: 'transform,opacity,visibility',
        scrollTrigger: { trigger: section, start: 'top 88%', once: true },
      }
    );

    if (featuredCards.length) {
      gsap.fromTo(
        featuredCards,
        { autoAlpha: 0, y: 40 * distanceScale, scale: 0.965 },
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: 0.82 * durationScale,
          stagger: 0.09 * staggerScale,
          ease: 'power2.out',
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility',
          scrollTrigger: {
            trigger: section.querySelector('[data-all-collections-featured]') || featuredCards[0],
            start: 'top 86%',
            once: true,
          },
        }
      );
    }

    if (gridCards.length) {
      gsap.fromTo(
        gridCards,
        { autoAlpha: 0, y: 24 * distanceScale },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.54 * durationScale,
          stagger: 0.045 * staggerScale,
          ease: 'power2.out',
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility',
          scrollTrigger: {
            trigger: section.querySelector('[data-all-collections-grid]') || gridCards[0],
            start: 'top 88%',
            once: true,
          },
        }
      );
    }

    if (ambientItems.length) {
      gsap.fromTo(
        ambientItems,
        { autoAlpha: 0.4, scale: 0.9 },
        {
          autoAlpha: 1,
          scale: 1,
          duration: 1.35 * durationScale,
          stagger: 0.08 * staggerScale,
          ease: 'power1.out',
          overwrite: 'auto',
          scrollTrigger: { trigger: section, start: 'top 92%', once: true },
        }
      );
    }
  });
}

function animateStoreReviewsKenz(root) {
  const gsap = window.gsap;
  if (!gsap) return;

  root.querySelectorAll('.store-reviews-kenz').forEach((section) => {
    if (shouldSkipNode(section)) return;

    const heroItems = section.querySelectorAll(
      '.store-reviews-kenz__eyebrow, .store-reviews-kenz h1, .store-reviews-kenz__intro'
    );
    const actionItems = section.querySelectorAll('.store-reviews-kenz__button, .store-reviews-kenz__note');
    const panel = section.querySelector('.store-reviews-kenz__panel');
    const panelHeadingItems = section.querySelectorAll('.store-reviews-kenz__panel-heading > *');
    const stepItems = section.querySelectorAll('.store-reviews-kenz__steps > div');
    const picker = section.querySelector('.store-reviews-kenz__picker, .store-reviews-kenz__app-placeholder');
    const glow = section.querySelector('.store-reviews-kenz__glow');

    section.setAttribute(MOTION_READY_ATTR, 'true');

    if (heroItems.length) {
      gsap.fromTo(
        heroItems,
        { autoAlpha: 0, y: 34 * distanceScale },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.72 * durationScale,
          stagger: 0.08 * staggerScale,
          ease: 'power2.out',
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility',
          scrollTrigger: { trigger: section, start: 'top 88%', once: true },
        }
      );
    }

    if (actionItems.length) {
      gsap.fromTo(
        actionItems,
        { autoAlpha: 0, y: 18 * distanceScale },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.5 * durationScale,
          stagger: 0.06 * staggerScale,
          delay: 0.18 * durationScale,
          ease: 'power2.out',
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility',
          scrollTrigger: { trigger: section, start: 'top 88%', once: true },
        }
      );
    }

    if (panel) {
      gsap.fromTo(
        panel,
        { autoAlpha: 0, x: 62 * distanceScale, scale: 0.975 },
        {
          autoAlpha: 1,
          x: 0,
          scale: 1,
          duration: 0.82 * durationScale,
          ease: 'power2.out',
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility',
          scrollTrigger: { trigger: panel, start: 'top 86%', once: true },
        }
      );
    }

    if (panelHeadingItems.length) {
      gsap.fromTo(
        panelHeadingItems,
        { autoAlpha: 0, y: 18 * distanceScale },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.48 * durationScale,
          stagger: 0.05 * staggerScale,
          delay: 0.18 * durationScale,
          ease: 'power2.out',
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility',
          scrollTrigger: { trigger: panel || section, start: 'top 86%', once: true },
        }
      );
    }

    if (stepItems.length) {
      gsap.fromTo(
        stepItems,
        { autoAlpha: 0, y: 20 * distanceScale, scale: 0.985 },
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: 0.5 * durationScale,
          stagger: 0.075 * staggerScale,
          delay: 0.24 * durationScale,
          ease: 'power2.out',
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility',
          scrollTrigger: { trigger: section.querySelector('.store-reviews-kenz__steps') || panel || section, start: 'top 86%', once: true },
        }
      );
    }

    if (picker) {
      gsap.fromTo(
        picker,
        { autoAlpha: 0, y: 24 * distanceScale },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.56 * durationScale,
          delay: 0.34 * durationScale,
          ease: 'power2.out',
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility',
          scrollTrigger: { trigger: picker, start: 'top 88%', once: true },
        }
      );
    }

    if (glow) {
      gsap.fromTo(
        glow,
        { autoAlpha: 0.08, scale: 0.82 },
        {
          autoAlpha: 0.16,
          scale: 1,
          duration: 1.4 * durationScale,
          ease: 'power1.out',
          overwrite: 'auto',
          scrollTrigger: { trigger: section, start: 'top 92%', once: true },
        }
      );
    }
  });
}

function animateFooter(root) {
  root.querySelectorAll('.footer-content > *, .utilities > *').forEach((node) => {
    revealOnScroll(node, { y: 14, duration: 0.44, start: 'top 98%' });
  });
}

document.addEventListener('shopify:section:load', () => {
  setTimeout(() => {
    window.lenis?.resize();
    window.ScrollTrigger?.refresh(true);
  }, 100);
});

document.addEventListener('shopify:section:reorder', () => {
  setTimeout(() => {
    window.lenis?.resize();
    window.ScrollTrigger?.refresh(true);
  }, 100);
});